import ky, { KyInstance, Options, HTTPError, TimeoutError } from 'ky'
import qs from 'qs'
import { ApiClient, ApiClientConfig, ApiClientResponse } from 'shared/services/ApiClient/interface'
import { ApiTimeoutError, ClientDisconnectedError, EmptyResponseError } from 'shared/services/ApiClient/errors'
import getLogger from 'shared/logger'
import config from 'config'
import { createApiError } from 'shared/services/ApiClient/utils'
import { isClientDisconnected } from '~/shared/isClientDisconnectedError'

const buildOptions = (data?: unknown, config?: ApiClientConfig): Options => {
  if (!config) {
    return data !== undefined ? { json: data } : {}
  }

  const { params, paramsSerializer, ...rest } = config

  const baseOptions = params
    ? {
        ...rest,
        searchParams: typeof paramsSerializer === 'function' ? paramsSerializer(params) : qs.stringify(params, paramsSerializer),
      }
    : rest

  return data !== undefined ? { json: data, ...baseOptions } : baseOptions
}

const makeRequest = async <T>(
  client: KyInstance,
  method: 'get' | 'post' | 'put' | 'patch' | 'delete',
  url: string,
  data?: unknown,
  config?: ApiClientConfig,
  serviceName?: string,
): Promise<ApiClientResponse<T>> => {
  const tag = serviceName ? `[${serviceName}] ` : ''

  try {
    const response = await client[method](url, buildOptions(data, config))

    if (method === 'delete' || response.status === 204) {
      return { data: undefined as T, status: response.status }
    }

    const responseData = await response.json<T>()

    if (!responseData) {
      throw new EmptyResponseError(url, response.status)
    }

    return { data: responseData, status: response.status }
  } catch (error) {
    const apiContext = { serviceName, method, url, params: config?.params, data }

    if (isClientDisconnected(error)) {
      getLogger().debug(`${tag}API Error - Client disconnected: ${error}`, apiContext)

      throw new ClientDisconnectedError(error)
    } else if (error instanceof HTTPError) {
      const apiError = createApiError(error, data, config?.params)

      getLogger().warn(`${tag}API Error - HTTP: (${apiError.status} ${apiError.statusText}): ${apiError.message}`, apiContext)

      throw apiError
    } else if (error instanceof TimeoutError) {
      getLogger().warn(`${tag}API Error - Timeout: ${error.message}`, apiContext)

      throw new ApiTimeoutError(error)
    } else {
      getLogger().warn(`${tag}API Error: ${error}`, apiContext)

      throw error
    }
  }
}

export const createApiClient = (options: Options = {}, serviceName?: string): ApiClient => {
  const clientOptions: Options = {
    ...config.services.global,
    ...options,
    retry: {
      ...config.services.global?.retry,
      ...(typeof options.retry === 'object' && options.retry),
    },
  }

  const client = ky.create(clientOptions)

  return {
    get: <T>(url: string, config?: ApiClientConfig) => makeRequest<T>(client, 'get', url, undefined, config, serviceName),
    post: <T>(url: string, data?: unknown, config?: ApiClientConfig) => makeRequest<T>(client, 'post', url, data, config, serviceName),
    put: <T>(url: string, data?: unknown, config?: ApiClientConfig) => makeRequest<T>(client, 'put', url, data, config, serviceName),
    patch: <T>(url: string, data?: unknown, config?: ApiClientConfig) => makeRequest<T>(client, 'patch', url, data, config, serviceName),
    delete: <T>(url: string, config?: ApiClientConfig) => makeRequest<T>(client, 'delete', url, undefined, config, serviceName),
  }
}
